import { useMemo } from 'react';

import { useI18n } from './provider';
import type { Locale } from './locale';

/**
 * Fechas, números y dinero atados al idioma activo.
 *
 * Antes había una docena de `toLocaleString()` sin argumentos repartidos por
 * las pantallas, que es exactamente lo contrario de lo que parece: sin locale
 * explícito, cada usuario veía el formato del navegador y no el del producto,
 * así que la misma marca de tiempo salía distinta en dos pestañas del mismo
 * escritorio. Ahora hay un solo sitio donde se decide.
 *
 * La zona horaria no se toca aquí. El backend guarda todo en UTC
 * (`00-README §4.4`) y la de presentación vive en `user.timezone`; ese es un
 * eje aparte que se resuelve pasando `timeZone` cuando haga falta, no
 * mezclándolo con el idioma.
 */

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;

/** El dinero se guarda en micro-USD enteros y se formatea sin pasar por float. */
function microToUnits(microUsd: number): number {
  return Math.floor(microUsd / 10_000) / 100;
}

export interface Format {
  /** `14 mar 2026` */
  date: (iso: string) => string;
  /** `14 mar 2026, 09:41` */
  dateTime: (iso: string) => string;
  /** `09:41` */
  time: (iso: string) => string;
  /** `hace 3 h`, `in 2 min` — el `Intl` nativo, no cadenas cosidas a mano. */
  relative: (iso: string, now?: number) => string;
  /** Un número con los separadores del idioma. */
  number: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** Micro-USD a `12,34 US$` o `$12.34` según el idioma. */
  money: (microUsd: number) => string;
  /** `43 %` */
  percent: (ratio: number, fractionDigits?: number) => string;
}

function build(locale: Locale): Format {
  const date = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  const dateTime = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
  const money = new Intl.NumberFormat(locale, { style: 'currency', currency: 'USD' });

  const parse = (iso: string): number | null => {
    const value = new Date(iso).getTime();

    return Number.isNaN(value) ? null : value;
  };

  return {
    date: (iso) => {
      const value = parse(iso);

      return value === null ? iso : date.format(value);
    },
    dateTime: (iso) => {
      const value = parse(iso);

      return value === null ? iso : dateTime.format(value);
    },
    time: (iso) => {
      const value = parse(iso);

      return value === null ? iso : time.format(value);
    },
    /**
     * Grueso a propósito, como lo era el `timeAgo` que sustituye: un reintento
     * programado para las 10:32:07 es «dentro de 10 min» para quien lo lee, y
     * cualquier cosa más fina es ruido que además se queda vieja mientras la
     * mira.
     */
    relative: (iso, now = Date.now()) => {
      const value = parse(iso);

      if (value === null) {
        return iso;
      }

      const delta = value - now;
      const magnitude = Math.abs(delta);

      if (magnitude < MINUTE) {
        return relative.format(0, 'second');
      }

      const [amount, unit]: [number, Intl.RelativeTimeFormatUnit] =
        magnitude < HOUR
          ? [delta / MINUTE, 'minute']
          : magnitude < DAY
            ? [delta / HOUR, 'hour']
            : [delta / DAY, 'day'];

      // Hacia cero: faltan «2 h», no «3 h», mientras no hayan pasado las tres.
      return relative.format(Math.trunc(amount) || (delta < 0 ? -1 : 1), unit);
    },
    number: (value, options) => (options ? new Intl.NumberFormat(locale, options) : new Intl.NumberFormat(locale)).format(value),
    money: (microUsd) => money.format(microToUnits(microUsd)),
    percent: (ratio, fractionDigits = 0) =>
      new Intl.NumberFormat(locale, {
        style: 'percent',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(ratio),
  };
}

/**
 * Los `Intl.*Format` son caros de construir y baratos de reutilizar, así que se
 * memorizan por idioma. Sin esto, una tabla de doscientas filas construiría
 * doscientos formateadores por repintado.
 */
export function useFormat(): Format {
  const { locale } = useI18n();

  return useMemo(() => build(locale), [locale]);
}

/** El mismo formateo fuera de React, para la capa de API. */
export const formatFor = build;
